import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Upload, CheckCircle, AlertCircle, Package, Shield, FileText,
  Info, X, ChevronDown, ChevronUp, CheckCircle2,
  Layers, Thermometer, Navigation2, HardDrive, Settings,
} from 'lucide-react';
import api, { missionsApi } from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { ImagePipelineMode } from '../../store/authStore';

// ─── File-type and scenario data (mirrors Flight Data & Analysis guidance) ────

interface FileTypeSpec {
  icon: React.ElementType;
  iconColour: string;
  label: string;
  formats: string;
  size: string;
  required: boolean;
  description: string;
}

const FILE_TYPES: FileTypeSpec[] = [
  {
    icon: Layers,
    iconColour: 'text-violet-600 dark:text-violet-400',
    label: 'Multispectral Imagery',
    formats: 'TIFF with band metadata',
    size: '100 MB – 500 MB',
    required: true,
    description:
      'Five-band capture (Blue 475 nm / Green 560 nm / Red 668 nm / Red Edge 717 nm / NIR 842 nm). ' +
      'Primary input for NDVI, NDRE, and NDWI computation. Required for any vegetation or crop-stress diagnosis.',
  },
  {
    icon: Upload,
    iconColour: 'text-blue-600 dark:text-blue-400',
    label: 'RGB Orthomosaic',
    formats: 'TIFF / JPG',
    size: '200 MB – 1 GB',
    required: true,
    description:
      'High-resolution true-colour mosaic from the RGB camera. Used to visually confirm canopy texture, ' +
      'bare-soil patches, physical damage, and pest density. Supports photogrammetric surface models.',
  },
  {
    icon: Thermometer,
    iconColour: 'text-orange-500 dark:text-orange-400',
    label: 'Thermal Imagery',
    formats: 'TIFF / RJPEG',
    size: '20 MB – 80 MB',
    required: false,
    description:
      'Radiometric JPEG or thermal TIFF capturing surface temperature. Identifies waterlogging, root-stress ' +
      '(stomatal disruption causes canopy heat elevation), and irrigation failure zones.',
  },
  {
    icon: FileText,
    iconColour: 'text-green-600 dark:text-green-400',
    label: 'LiDAR Point Cloud',
    formats: 'LAS / LAZ',
    size: '500 MB – 5 GB',
    required: false,
    description:
      'Dense 3-D point cloud for canopy height, canopy gap fraction, and under-canopy structure. ' +
      'Required for forestry, orchard, and plantation vertical-structure assessments.',
  },
  {
    icon: Navigation2,
    iconColour: 'text-teal-600 dark:text-teal-400',
    label: 'Flight Log',
    formats: 'CSV / KML / DJI .DAT',
    size: '1 MB – 10 MB',
    required: true,
    description:
      'Full telemetry log exported from the drone (GPS path, altitude, speed, wind, battery %). ' +
      'Required for integrity verification, area-coverage confirmation, and audit chain.',
  },
];

interface Scenario {
  anomalyType: string;
  colour: string;
  textColour: string;
  files: string[];
  payloadNote: string;
}

const SCENARIOS: Scenario[] = [
  {
    anomalyType: 'Vegetation Decline',
    colour: 'bg-red-100 dark:bg-red-900/30',
    textColour: 'text-red-700 dark:text-red-300',
    files: ['Multispectral Imagery', 'RGB Orthomosaic', 'Thermal Imagery', 'Flight Log'],
    payloadNote: 'MicaSense RedEdge-P (5-band) + FLIR Vue Pro R. Altitude 90–100 m.',
  },
  {
    anomalyType: 'Water Stress / NDWI Anomaly',
    colour: 'bg-blue-100 dark:bg-blue-900/30',
    textColour: 'text-blue-700 dark:text-blue-300',
    files: ['Multispectral Imagery', 'Thermal Imagery', 'Flight Log'],
    payloadNote: 'Multispectral + Thermal at 80–100 m. NDWI maps moisture; thermal pinpoints waterlogging.',
  },
  {
    anomalyType: 'Pest / Infestation Indicator',
    colour: 'bg-amber-100 dark:bg-amber-900/30',
    textColour: 'text-amber-700 dark:text-amber-300',
    files: ['Multispectral Imagery', 'RGB Orthomosaic', 'Flight Log'],
    payloadNote: 'High-res multispectral at 60–80 m. Reduced GSD (~3 cm) for canopy texture classification.',
  },
  {
    anomalyType: 'Bare Soil / Erosion',
    colour: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColour: 'text-yellow-700 dark:text-yellow-300',
    files: ['RGB Orthomosaic', 'Multispectral Imagery', 'Flight Log'],
    payloadNote: 'RGB photogrammetric survey + multispectral to confirm NDVI depression. Altitude 80–100 m.',
  },
  {
    anomalyType: 'Canopy Height / Structure',
    colour: 'bg-emerald-100 dark:bg-emerald-900/30',
    textColour: 'text-emerald-700 dark:text-emerald-300',
    files: ['LiDAR Point Cloud', 'RGB Orthomosaic', 'Flight Log'],
    payloadNote: 'LiDAR (Zenmuse L1 or equivalent) + RGB. Orchard/plantation vertical structure analysis.',
  },
  {
    anomalyType: 'General Crop Health Screening',
    colour: 'bg-purple-100 dark:bg-purple-900/30',
    textColour: 'text-purple-700 dark:text-purple-300',
    files: ['Multispectral Imagery', 'Flight Log'],
    payloadNote: 'Standard multispectral at 100–120 m for large-area NDVI vigour screening.',
  },
];

// ─── Inline collapsible file-type row ─────────────────────────────────────────

function FileTypeRow({ spec, highlighted }: { spec: FileTypeSpec; highlighted: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = spec.icon;
  return (
    <div className={`rounded-lg border transition-colors ${
      highlighted
        ? 'border-violet-300 bg-violet-50 dark:border-violet-600/50 dark:bg-violet-900/20'
        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50'
    }`}>
      <button type="button" onClick={() => setExpanded(v => !v)} className="w-full flex items-center gap-3 p-3 text-left">
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          highlighted ? 'bg-violet-100 dark:bg-violet-800/40' : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          <Icon className={`w-4 h-4 ${spec.iconColour}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{spec.label}</span>
            {spec.required ? (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">Required</span>
            ) : (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Conditional</span>
            )}
            {highlighted && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Needed for this scenario
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {spec.formats}<span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>{spec.size}
          </p>
        </div>
        <div className="flex-shrink-0 text-gray-400">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-0 ml-11">
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{spec.description}</p>
        </div>
      )}
    </div>
  );
}

// ─── Upload guidance panel (toggled by the ⓘ button) ─────────────────────────

function UploadGuidancePanel({ onClose }: { onClose: () => void }) {
  const [activeScenario, setActiveScenario] = useState<string | null>(null);
  const highlighted = activeScenario
    ? (SCENARIOS.find(s => s.anomalyType === activeScenario)?.files ?? [])
    : [];

  return (
    <div className="mt-4 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-4">
      {/* Panel header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">Upload Guidance — File Types &amp; Scenarios</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-800/40 text-blue-500 dark:text-blue-400"
          aria-label="Close upload guidance"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Scenario picker */}
      <div>
        <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">
          Select a scenario to highlight the required files:
        </p>
        <div className="flex flex-wrap gap-2">
          {SCENARIOS.map(s => (
            <button
              key={s.anomalyType}
              type="button"
              onClick={() => setActiveScenario(prev => prev === s.anomalyType ? null : s.anomalyType)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                activeScenario === s.anomalyType
                  ? `${s.colour} ${s.textColour} border-current ring-1 ring-current`
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-400'
              }`}
            >
              {s.anomalyType}
            </button>
          ))}
        </div>
        {activeScenario && (() => {
          const sc = SCENARIOS.find(s => s.anomalyType === activeScenario)!;
          return (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-300 italic">
              {sc.payloadNote}
            </p>
          );
        })()}
      </div>

      {/* File type list */}
      <div className="space-y-2">
        {FILE_TYPES.map(spec => (
          <FileTypeRow
            key={spec.label}
            spec={spec}
            highlighted={highlighted.includes(spec.label)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── File upload section with guidance toggle ─────────────────────────────────

interface FileUploadSectionProps {
  selectedFiles: File[];
  flightLogFile: File | null;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFlightLogSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function FileUploadSection({ selectedFiles, flightLogFile, onFileSelect, onFlightLogSelect }: FileUploadSectionProps) {
  const [showGuidance, setShowGuidance] = useState(false);
  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Upload className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">Upload Files</h2>
        <button
          type="button"
          onClick={() => setShowGuidance(v => !v)}
          className={`p-1.5 rounded-lg transition-colors ${
            showGuidance
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-800/50 dark:text-blue-300'
              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30'
          }`}
          aria-label={showGuidance ? 'Hide upload guidance' : 'Show upload guidance'}
          title="What files to upload and when"
        >
          <Info className="w-4 h-4" />
        </button>
      </div>

      {showGuidance && <UploadGuidancePanel onClose={() => setShowGuidance(false)} />}

      <div className="space-y-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Mission Images *
          </label>
          <input
            type="file"
            multiple
            accept="image/*,.tif,.tiff"
            onChange={onFileSelect}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <p className="mt-1 text-xs text-gray-400">
            TIFF (multispectral / RGB orthomosaic / thermal) or JPG images from the drone payload
          </p>
          {selectedFiles.length > 0 && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {selectedFiles.length} file(s) selected
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Flight Log (Optional)
          </label>
          <input
            type="file"
            accept=".log,.txt,.csv,.kml,.dat"
            onChange={onFlightLogSelect}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <p className="mt-1 text-xs text-gray-400">
            CSV / KML / DJI .DAT telemetry export — required for integrity verification and audit chain
          </p>
          {flightLogFile && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {flightLogFile.name}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

interface ApprovedAsset {
  id: string;
  asset_type: 'drone' | 'sensor' | 'camera';
  manufacturer: string;
  model: string;
  serial_number: string;
  last_calibration_date?: string;
  calibration_interval_days?: number;
  status: 'active' | 'maintenance' | 'retired';
}

interface HardwareMetadata {
  drone_serial_number: string;
  sensor_calibration_date?: string;
  pilot_id?: string;
  flight_log_path?: string;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

interface IngestionJob {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  error_message?: string | null;
  result?: {
    uploaded_files: number;
    integrity_seal: string;
  } | null;
}

export default function MissionIngestionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { imagePipeline } = useOrgSettings();

  const [mission, setMission] = useState<any>(null);
  const [drones, setDrones] = useState<ApprovedAsset[]>([]);
  const [sensors, setSensors] = useState<ApprovedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  // Pipeline mode — seeded from org default, overridable if org allows
  const [pipelineMode, setPipelineMode] = useState<ImagePipelineMode>(imagePipeline.default_mode);
  
  const [hardwareMetadata, setHardwareMetadata] = useState<HardwareMetadata>({
    drone_serial_number: '',
    sensor_calibration_date: '',
    pilot_id: '',
    flight_log_path: '',
  });
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [flightLogFile, setFlightLogFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [currentJob, setCurrentJob] = useState<IngestionJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load mission details
      const missionRes = await api.get(`/missions/${id}`);
      setMission(missionRes.data.data.mission);
      
      // Load approved drones
      const dronesRes = await api.get('/approved-assets', {
        params: { asset_type: 'drone', status: 'active' }
      });
      setDrones(dronesRes.data.data.assets);
      
      // Load approved sensors
      const sensorsRes = await api.get('/approved-assets', {
        params: { asset_type: 'sensor', status: 'active' }
      });
      setSensors(sensorsRes.data.data.assets);
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateHardware = async () => {
    if (!hardwareMetadata.drone_serial_number) {
      setError('Please select a drone');
      return;
    }

    try {
      setValidating(true);
      const response = await api.post('/missions/validate-hardware', {
        drone_serial_number: hardwareMetadata.drone_serial_number,
        sensor_calibration_date: hardwareMetadata.sensor_calibration_date,
      });
      
      setValidationResult(response.data.data.validation);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Hardware validation failed');
      setValidationResult(null);
    } finally {
      setValidating(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleFlightLogSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFlightLogFile(e.target.files[0]);
    }
  };

  const sha256 = async (buffer: ArrayBuffer) => {
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const uploadViaSession = async (file: File) => {
    const fileBuffer = await file.arrayBuffer();
    const fileChecksum = await sha256(fileBuffer);

    const sessionResponse = await api.post('/uploads/sessions', {
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || 'application/octet-stream',
      file_checksum: fileChecksum,
    });

    const { session_id, total_chunks } = sessionResponse.data.data;
    const chunkSize = 5 * 1024 * 1024;

    for (let chunkIndex = 0; chunkIndex < total_chunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunkBlob = file.slice(start, end);
      const chunkBuffer = await chunkBlob.arrayBuffer();
      const chunkChecksum = await sha256(chunkBuffer);
      const formData = new FormData();
      formData.append('chunk', chunkBlob, file.name);
      formData.append('chunk_checksum', chunkChecksum);
      await api.post(`/uploads/sessions/${session_id}/chunks/${chunkIndex}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }

    await api.post(`/uploads/sessions/${session_id}/finalize`);
    return session_id as string;
  };

  const handleIngest = async () => {
    if (!validationResult?.valid) {
      setError('Please validate hardware before ingesting data');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one image file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const sessionIds: string[] = [];
      for (const file of selectedFiles) {
        sessionIds.push(await uploadViaSession(file));
      }
      if (flightLogFile) {
        sessionIds.push(await uploadViaSession(flightLogFile));
      }

      const response = await missionsApi.createIngestionJob(id!, {
        upload_session_ids: sessionIds,
        hardware_metadata: {
          drone_serial_number: hardwareMetadata.drone_serial_number,
          sensor_calibration_date: hardwareMetadata.sensor_calibration_date,
          pilot_id: hardwareMetadata.pilot_id,
        },
        pipeline_mode:      pipelineMode,
        processing_engine:  imagePipeline.processing_engine,
        raw_format:         imagePipeline.raw_format,
      });

      const job = response.data as IngestionJob;
      setCurrentJob(job);
      setSuccess('Ingestion job queued successfully. Processing will continue asynchronously.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to queue ingestion job');
    } finally {
      setUploading(false);
    }
  };

  const getCalibrationStatus = (sensor: ApprovedAsset) => {
    if (!sensor.last_calibration_date || !sensor.calibration_interval_days) {
      return { status: 'unknown', color: 'gray', text: 'Unknown' };
    }

    const lastCalibration = new Date(sensor.last_calibration_date);
    const now = new Date();
    const daysSince = Math.floor(
      (now.getTime() - lastCalibration.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysUntilDue = sensor.calibration_interval_days - daysSince;

    if (daysUntilDue < 0) {
      return { status: 'overdue', color: 'red', text: `Overdue by ${Math.abs(daysUntilDue)} days` };
    } else if (daysUntilDue <= 30) {
      return { status: 'due_soon', color: 'yellow', text: `Due in ${daysUntilDue} days` };
    } else {
      return { status: 'current', color: 'green', text: `Current (${daysUntilDue} days)` };
    }
  };

  useEffect(() => {
    if (!currentJob?.id || currentJob.status === 'completed' || currentJob.status === 'failed') {
      return;
    }

    const timer = window.setInterval(async () => {
      try {
        const response = await missionsApi.getIngestionJob(currentJob.id);
        const job = response.data as IngestionJob;
        setCurrentJob(job);
        if (job.status === 'completed') {
          setSuccess('Mission data ingested successfully with integrity seal!');
          window.clearInterval(timer);
          setTimeout(() => {
            navigate(`/missions/${id}`);
          }, 2000);
        }
        if (job.status === 'failed') {
          setError(job.error_message || 'Ingestion job failed');
          window.clearInterval(timer);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to poll ingestion job');
        window.clearInterval(timer);
      }
    }, 5000);

    return () => window.clearInterval(timer);
  }, [currentJob?.id, currentJob?.status, id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mission Data Ingestion</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Upload mission data with hardware validation and integrity sealing
        </p>
        {mission && (
          <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Mission: {mission.name}
          </p>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
        </div>
      )}

      <div className="space-y-6">
        {currentJob && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Ingestion Job Status</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">Job ID: {currentJob.id}</p>
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">Status: {currentJob.status}</p>
            {currentJob.error_message && (
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">{currentJob.error_message}</p>
            )}
          </div>
        )}
        {/* Hardware Selection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Hardware Selection</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Drone *
              </label>
              <select
                value={hardwareMetadata.drone_serial_number}
                onChange={(e) => setHardwareMetadata({ ...hardwareMetadata, drone_serial_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="">Select a drone</option>
                {drones.map((drone) => (
                  <option key={drone.id} value={drone.serial_number}>
                    {drone.manufacturer} {drone.model} ({drone.serial_number})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sensor (Optional)
              </label>
              <select
                value={hardwareMetadata.sensor_calibration_date || ''}
                onChange={(e) => {
                  const sensor = sensors.find(s => s.last_calibration_date === e.target.value);
                  setHardwareMetadata({ 
                    ...hardwareMetadata, 
                    sensor_calibration_date: e.target.value 
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">No sensor selected</option>
                {sensors.map((sensor) => {
                  const calibStatus = getCalibrationStatus(sensor);
                  return (
                    <option key={sensor.id} value={sensor.last_calibration_date}>
                      {sensor.manufacturer} {sensor.model} - {calibStatus.text}
                    </option>
                  );
                })}
              </select>
              {hardwareMetadata.sensor_calibration_date && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Last calibrated: {new Date(hardwareMetadata.sensor_calibration_date).toLocaleDateString()}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Pilot ID (Optional)
              </label>
              <input
                type="text"
                value={hardwareMetadata.pilot_id}
                onChange={(e) => setHardwareMetadata({ ...hardwareMetadata, pilot_id: e.target.value })}
                placeholder="Enter pilot identifier"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <button
              onClick={handleValidateHardware}
              disabled={validating || !hardwareMetadata.drone_serial_number}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Shield className="w-4 h-4" />
              {validating ? 'Validating...' : 'Validate Hardware'}
            </button>
          </div>

          {validationResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              validationResult.valid 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {validationResult.valid ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    validationResult.valid 
                      ? 'text-green-800 dark:text-green-200' 
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                    {validationResult.valid ? 'Hardware Validated Successfully' : 'Hardware Validation Failed'}
                  </p>
                  {validationResult.errors.length > 0 && (
                    <ul className="mt-2 text-sm text-red-700 dark:text-red-300 space-y-1">
                      {validationResult.errors.map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                    </ul>
                  )}
                  {validationResult.warnings.length > 0 && (
                    <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                      {validationResult.warnings.map((warn, idx) => (
                        <li key={idx}>⚠ {warn}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Pipeline Mode ── */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-indigo-500" />
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Upload Mode</h2>
            </div>
            {!imagePipeline.allow_per_mission_override && (
              <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Org default enforced
              </span>
            )}
            {imagePipeline.allow_per_mission_override && (
              <a href="/settings/flight-data-pipeline" className="text-xs text-indigo-500 hover:underline flex items-center gap-1">
                <Settings className="w-3 h-3" /> Change org default
              </a>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Org default: <strong>{imagePipeline.default_mode === 'upload_processed' ? 'Upload processed outputs' : imagePipeline.default_mode === 'upload_raw' ? 'Upload raw images' : 'Upload both'}</strong>
            {imagePipeline.default_mode !== 'upload_processed' && (
              <> · {imagePipeline.processing_engine === 'external' ? 'External processing' : imagePipeline.processing_engine === 'odm' ? 'ODM pipeline' : 'Platform pipeline'} · {imagePipeline.raw_format.toUpperCase()}</>
            )}
          </p>
          <div className="space-y-2">
            {([
              { value: 'upload_processed' as const, label: 'Upload processed outputs',
                hint: 'Orthomosaics, GeoTIFFs, point clouds, DEMs — already processed externally.' },
              { value: 'upload_raw'       as const, label: 'Upload raw drone images',
                hint: `Raw ${imagePipeline.raw_format.toUpperCase()} files from SD card — platform or ODM handles processing.` },
              { value: 'upload_both'      as const, label: 'Upload both',
                hint: 'Raw images + processed outputs stored together for full traceability.' },
            ] as const).map(opt => (
              <label
                key={opt.value}
                className={[
                  'flex items-start gap-3 px-4 py-3 rounded-lg border-2 transition-all',
                  imagePipeline.allow_per_mission_override ? 'cursor-pointer' : 'cursor-not-allowed opacity-80',
                  pipelineMode === opt.value
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="pipeline_mode"
                  value={opt.value}
                  checked={pipelineMode === opt.value}
                  disabled={!imagePipeline.allow_per_mission_override}
                  onChange={() => setPipelineMode(opt.value)}
                  className="mt-0.5 accent-indigo-500 flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{opt.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.hint}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <FileUploadSection
            selectedFiles={selectedFiles}
            flightLogFile={flightLogFile}
            onFileSelect={handleFileSelect}
            onFlightLogSelect={handleFlightLogSelect}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate(`/missions/${id}`)}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleIngest}
            disabled={uploading || !validationResult?.valid || selectedFiles.length === 0 || currentJob?.status === 'queued' || currentJob?.status === 'processing'}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-4 h-4" />
            {uploading ? 'Uploading & Queueing...' : 'Queue Ingestion Job'}
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            🔒 Data Integrity & Security
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>• All files are sealed with SHA-256 cryptographic hashes</li>
            <li>• Hardware validation ensures only approved assets are used</li>
            <li>• Flight logs are correlated with mission data for traceability</li>
            <li>• Integrity verification available at any time</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// Made with Bob